import { addMonths, addWeeks, DateOnly } from '@if/core/date'
import { t } from '@if/core/i18n'
import { EPaymentFrequency } from '@if/core/types'
import { dateOnly, InferSubmit, integer, mixed, ref } from '@if/core/yup'
import { useIfUser } from '@if/mui'
import { SafePaymentActionDateService } from '@src/services/SafePaymentActionDateService'
import { DEFAULT_PAYMENT_METHOD, EPaymentMethod } from '@src/types/constants'
import { lazyLoad } from '@src/types/lazy'
import { useMemo } from 'react'
import { getEditPaymentSchema } from '../EditPayments-schema'

export const getAddRecurringPaymentSupportedFrequencies = lazyLoad(() => [
  EPaymentFrequency.Monthly,
  EPaymentFrequency.BiWeekly,
  EPaymentFrequency.Weekly,
])

export enum EAddRecurringPaymentOption {
  EndDate = 'EndDate',
  NumberOfPayments = 'NumberOfPayments',
}

export const getAddRecurringPaymentSchema = (
  loanActivationDate: DateOnly,
  loanEndDate: DateOnly,
  canBypassAcpLock = false,
) =>
  getEditPaymentSchema(loanActivationDate, canBypassAcpLock)
    .pick(['paymentMethod', 'sendSmsReminder', 'note', 'amount'])
    .shape({
      frequency: mixed<EPaymentFrequency>()
        .oneOf(getAddRecurringPaymentSupportedFrequencies())
        .required()
        .default(EPaymentFrequency.BiWeekly),
      startDate: dateOnly()
        .required()
        .test(
          'minimum-safe-date',
          () => t('loan.payment.addRecurringPayments.errors.minStartDate'),
          (value, ctx) => {
            const paymentMethod = ctx.resolve<EPaymentMethod>(ref('paymentMethod')) ?? DEFAULT_PAYMENT_METHOD
            const minimumSafeDate = SafePaymentActionDateService.getInstance().getMinPaymentDate(
              paymentMethod,
              loanActivationDate,
              canBypassAcpLock,
            )
            return value >= minimumSafeDate
          },
        )
        .default(
          SafePaymentActionDateService.getInstance().getMinPaymentDate(
            DEFAULT_PAYMENT_METHOD,
            loanActivationDate,
            canBypassAcpLock,
          ),
        ),
      option: mixed<EAddRecurringPaymentOption>()
        .oneOf([EAddRecurringPaymentOption.NumberOfPayments, EAddRecurringPaymentOption.EndDate])
        .default(EAddRecurringPaymentOption.NumberOfPayments),
      numberOfPayments: integer()
        .notRequired()
        .min(1, () => t('loan.payment.addRecurringPayments.errors.noPayments'))
        .max(30, () => t('loan.payment.addRecurringPayments.errors.tooManyPayments'))
        .test(
          'is-valid-end-date',
          () => t('loan.payment.addRecurringPayments.errors.endDateAfterLoanEndDate', { date: loanEndDate }),
          (value, ctx) => {
            if (!value) return true
            const startDate = ctx.resolve<DateOnly | null>(ref('startDate'))
            if (!startDate) return true
            const frequency = ctx.resolve<EPaymentFrequency | null>(ref('frequency'))
            if (!frequency) return true
            let maxEndDate: DateOnly
            if (frequency === EPaymentFrequency.Monthly) {
              maxEndDate = addMonths(startDate, value) // 1 month per payment for monthly
            } else {
              maxEndDate = addWeeks(startDate, frequency === EPaymentFrequency.Weekly ? value : value * 2) // 1 week per payment for weekly, 2 weeks per payment for bi-weekly
            }
            return maxEndDate <= loanEndDate
          },
        )
        .default(null)
        .when('option', {
          is: EAddRecurringPaymentOption.NumberOfPayments,
          then: (schema) => schema.required(),
        }),
      endDate: dateOnly()
        .notRequired()
        .default(null)
        .min(ref('startDate'), () => t('loan.payment.addRecurringPayments.errors.endDateBeforeStartDate'))
        .test(
          'is-valid-end-date',
          () => t('loan.payment.addRecurringPayments.errors.endDateAfterLoanEndDate', { date: loanEndDate }),
          (value) => {
            if (!value) return true
            return value <= loanEndDate
          },
        )
        .test(
          'is-too-far',
          () => t('loan.payment.addRecurringPayments.errors.endDateTooFar'),
          (value, ctx) => {
            if (!value) return true
            const startDate = ctx.resolve<DateOnly | null>(ref('startDate'))
            if (!startDate) return true
            const maxEndDate = addWeeks(startDate, 30) // 30 weeks to simulate the max 30 payments of weekly frequency
            return value <= maxEndDate
          },
        )
        .when('option', {
          is: EAddRecurringPaymentOption.EndDate,
          then: (schema) => schema.required(),
        }),
    })

export type AddRecurringPaymentSchemaType = InferSubmit<ReturnType<typeof getAddRecurringPaymentSchema>>

export const useAddRecurringPaymentSchema = (loanActivationDate: DateOnly, loanEndDate: DateOnly) => {
  const user = useIfUser()
  const canBypassAcpLock = user.rights.canEditLockedPpaPayments
  return useMemo(
    () => getAddRecurringPaymentSchema(loanActivationDate, loanEndDate, canBypassAcpLock),
    [loanActivationDate, loanEndDate, canBypassAcpLock],
  )
}
